import { ref } from "vue";
import { UNDER_SESSION_SHARE, USAGE_STALE_MS, WEEK_MS, currentOutside, fiveHourElapsed, usageState, weekElapsed, weeklyHoursFlatOut, type UsageState } from "@shared/usage.js";
import type { StartMode } from "@shared/startMode.js";
import { HOUR_MS, type ClaudeUsage } from "@shared/types.js";
import { api } from "./api.js";

/**
 * The `claude` account's plan usage, one copy for the page: the Claude strip
 * draws it, and the git panel's Under / Wastage chips colour by it, so the two
 * say the same thing about whether those are sending.
 */
export const claudeUsage = ref<ClaudeUsage | null>(null);

/** The runner reports every five minutes: a reading this young is as good as a new one. */
const USAGE_FRESH_MS = 30_000;
let usageAt = 0;
let usageOut: Promise<void> | null = null;

/** Reads it — once for every caller at the same time (the strip, the git panel,
 *  the stats view), and not again within `USAGE_FRESH_MS` unless `force`d.
 *  Never throws: on a failure the last reading stays, and the stale fade says
 *  how old it is. */
export function loadClaudeUsage(force = false): Promise<void> {
  if (usageOut) return usageOut;
  if (!force && Date.now() - usageAt < USAGE_FRESH_MS) return Promise.resolve();
  usageOut = (async () => {
    try {
      claudeUsage.value = (await api.get<{ usage: ClaudeUsage | null }>("/api/claude-usage")).usage;
      usageAt = Date.now();
    } catch {
      // Kept as it was; the next call asks again.
    } finally {
      usageOut = null;
    }
  })();
  return usageOut;
}

/** Tests only: forget the last read. */
export function forgetClaudeUsage(): void {
  usageAt = 0;
  usageOut = null;
  claudeUsage.value = null;
}

/** Each usage state (`usageState`, shared/usage.ts) as the strip and the chips name it. */
export const USAGE_STATE_LABELS: Record<UsageState, string> = {
  unknown: "Unknown",
  atLimit: "At limit",
  wastage: "Wastage",
  over: "Over",
  held: "Held",
  under: "Under",
};

export const USAGE_STATE_TITLES: Record<UsageState, string> = {
  unknown: "Unknown: no fresh usage reading, so nothing started Under or Wastage is sent",
  atLimit: "At limit: the week's allowance or this 5-hour window is used up — nothing can be sent",
  wastage: "Wastage: fewer hours left in the week than even flat-out use needs to spend what is left — allowance is being lost, so Under and Wastage both send, without pacing the 5-hour window",
  over: "Over: more of the week's allowance used than of the week gone — Under and Wastage wait",
  held: "Held: under the week's pace, but this 5-hour window has used more than half its share of the time gone — Under waits",
  under: "Under: under the week's pace, with room in this 5-hour window — Under sends",
};

/** How a row started Under or Wastage stands in `state`: `sending`, `held` by
 *  the five-hour window or by another Under task still being worked on
 *  (`underBusy`: only so many at a time outside wastage, server/underRuns.ts), or
 *  `waiting` — or null with no fresh reading to say. */
export function usageChip(mode: StartMode, state: UsageState, underBusy = false): "sending" | "held" | "waiting" | null {
  if (state === "unknown") return null;
  if (state === "wastage") return "sending";
  if (mode === "under" && state === "under") return underBusy ? "held" : "sending";
  if (mode === "under" && state === "held") return "held";
  return "waiting";
}

/** A stretch of the strip's five-hour bar drawn over its fill, in the bar's own percent. */
export interface BarSegment {
  key: "spare" | "ahead" | "outside";
  left: number;
  width: number;
  /** `outside` only: more was spent elsewhere than the share of the window gone. */
  over?: boolean;
}

/**
 * The strip's five-hour bar against the window's TIME: how far through it now
 * is (`time`, its tick; null with no window open) and what to draw over the
 * fill. Between use and time, the difference: `spare` (green) where time has
 * run on past what was used, `ahead` (yellow) where use has run on past the
 * time. And from the start, what was used off the box (`outside`, orange — it
 * is part of the fill, so never drawn longer), red once that alone is more
 * than the share of the window gone.
 */
export function fiveHourBar(usage: ClaudeUsage | null, now: number): { time: number | null; segments: BarSegment[] } {
  const f = usage?.fiveHour;
  const time = fiveHourElapsed(f?.resetsAt, now);
  if (!f || time === null) return { time: null, segments: [] };
  const used = Math.min(100, Math.max(0, f.percent));
  const segments: BarSegment[] = [];
  if (used < time) segments.push({ key: "spare", left: used, width: time - used });
  else if (used > time) segments.push({ key: "ahead", left: time, width: used - time });
  const outside = currentOutside(usage, now);
  // Over the rest, so it goes last.
  if (outside && used > 0) segments.push({ key: "outside", left: 0, width: Math.min(outside.points, used), over: outside.points > time });
  return { time, segments };
}

/**
 * The strip's weekly bar, tokens against time as the five-hour one: `time` is
 * how far through the week now is (its tick; null when the reading does not say
 * when it resets), with the same `spare` / `ahead` difference between that and
 * what is used. `reserve` is its last stretch, off the right edge: the share of
 * the week that spending what is left flat out would take (`weeklyHoursFlatOut`)
 * — and `wasted`, how far the week's time has run into it: wastage.
 */
export function weeklyBar(usage: ClaudeUsage | null, now: number): { time: number | null; segments: BarSegment[]; reserve: number | null; wasted: number } {
  const w = usage?.weekly;
  const time = weekElapsed(w?.resetsAt, now);
  if (!w || time === null) return { time: null, segments: [], reserve: null, wasted: 0 };
  const used = Math.min(100, Math.max(0, w.percent));
  const segments: BarSegment[] = [];
  if (used < time) segments.push({ key: "spare", left: used, width: time - used });
  else if (used > time) segments.push({ key: "ahead", left: time, width: used - time });
  const hours = weeklyHoursFlatOut(usage);
  const reserve = hours ? Math.min(100, ((hours * HOUR_MS) / WEEK_MS) * 100) : null;
  return { time, segments, reserve, wasted: reserve === null ? 0 : Math.max(0, time - (100 - reserve)) };
}

/** One figure in the stats view's summary: `tone` colours the value as the strip would. */
export interface UsageDetail {
  key: string;
  label: string;
  value: string;
  sub: string;
  tone?: "ok" | "held" | "danger" | "ultra";
}

/** "3h05", "42m", "now". */
export function spanLeft(ms: number): string {
  const minutes = Math.ceil(ms / 60_000);
  if (minutes <= 0) return "now";
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}`;
}

const pct = (v: number): string => `${v > 0 && v < 1 ? "<1" : Math.round(v)}%`;

/**
 * The figures behind the usage state, for the top of the stats view: the week
 * against its pace (and, over it, how long until back on it), how long
 * flat-out use would take to spend what is left (and so how long until
 * wastage), the five-hour window against the half pace Under keeps, use elsewhere, and how old the reading is. Empty with no reading.
 */
export function usageDetails(usage: ClaudeUsage | null, now: number): UsageDetail[] {
  if (!usage) return [];
  const out: UsageDetail[] = [];
  const w = usage.weekly;
  if (w) {
    const through = weekElapsed(w.resetsAt, now);
    out.push({ key: "week", label: "Weekly used", value: pct(w.percent), sub: `${pct(Math.max(0, 100 - w.percent))} left` });
    if (through !== null && w.resetsAt) {
      const ahead = w.percent - through;
      out.push({ key: "gone", label: "Week gone", value: pct(through), sub: `resets in ${spanLeft(w.resetsAt - now)}` });
      out.push({
        key: "pace",
        label: "Pace",
        value: Math.round(ahead) === 0 ? "on pace" : `${Math.abs(Math.round(ahead))} pts ${ahead > 0 ? "over" : "under"}`,
        // Over, the week catches up at one point per hundredth of it: the wait until on pace again.
        sub: ahead > 0 ? `on pace in ${spanLeft((ahead / 100) * WEEK_MS)}: Under and Wastage wait` : "used vs week gone",
        tone: ahead > 10 ? "danger" : ahead > 0 ? undefined : "ok",
      });
      const flat = weeklyHoursFlatOut(usage) ?? 0;
      const left = (w.resetsAt - now) / HOUR_MS;
      const toWaste = left - flat;
      out.push({
        key: "flat",
        label: "Flat out needs",
        value: `${flat}h`,
        sub: w.percent >= 100 ? "nothing left to spend" : toWaste < 0 ? `only ${Math.floor(left)}h left: wasting now` : `wastage in ${spanLeft(toWaste * HOUR_MS)}`,
        tone: toWaste < 0 && w.percent < 100 ? "ultra" : undefined,
      });
    }
  }
  const f = usage.fiveHour;
  if (f) {
    const through = fiveHourElapsed(f.resetsAt, now);
    const open = through !== null;
    out.push({
      key: "five",
      label: "5-hour used",
      value: pct(open ? f.percent : 0),
      sub: open ? `resets in ${spanLeft(f.resetsAt! - now)}` : "no window open",
      tone: open && f.percent >= 100 ? "danger" : undefined,
    });
    if (through !== null) {
      const room = through * UNDER_SESSION_SHARE;
      const held = f.percent > 0 && f.percent >= room;
      out.push({
        key: "room",
        label: "Window pace",
        value: `room to ${pct(room)}`,
        sub: `${pct(through)} of the window gone${held ? ": Under is held" : ""}`,
        tone: held ? "held" : "ok",
      });
    }
  }
  const outside = currentOutside(usage, now);
  if (outside) out.push({ key: "outside", label: "Used elsewhere", value: `+${pct(outside.points)}`, sub: "this 5-hour window, off this box", tone: "held" });
  if (usage.readAt) {
    const age = now - usage.readAt;
    out.push({
      key: "read",
      label: "Read",
      // Whole minutes gone, rounded down: spanLeft rounds up, as a time to go should.
      value: age < 60_000 ? "just now" : `${spanLeft(Math.floor(age / 60_000) * 60_000)} ago`,
      sub: usage.error ? `last read failed: ${usage.error}` : age > USAGE_STALE_MS ? "stale: nothing is sent on it" : "the runner reports every 5 min",
      tone: usage.error || age > USAGE_STALE_MS ? "danger" : undefined,
    });
  }
  return out;
}
